import { Flex } from '@chakra-ui/react';
import { Button } from '@foam/buoy/src/components/Button/Button';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { MagicButton } from '@foam/buoy/src/components/MagicButton/MagicButton';
import { useSelector } from 'react-redux';
import type { ContentDetailModalTrackIdType } from '~/components/ContentDetailModal';
import {
  CONTENT_DETAIL_MODAL_TRACK_ID,
  ONBOARDING_HOOKS_TRACK_ID,
} from '~/components/ContentDetailModal/constants';
import { ExportPdfButton } from '~/components/ExportPdfButton';
import {
  FEATURE_FLAGS,
  PDF_DOCUMENT_TYPE,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { ONBOARDING_HOOKS_ACTION_TYPES } from '~/constants/extension';
import { useConversationStarterAction } from '~/hooks/useConversationStarterAction';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import type { CreatorInfo } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import type { ISearchContentResult } from '~/searchApi';
import { selectTalentManager } from '~/selectors/talent-manager';

export interface Props {
  content: ISearchContentResult;
  creator: CreatorInfo;
  onShare: (content: ISearchContentResult) => void;
  trackIds: ContentDetailModalTrackIdType;
  hideSimilarPostsButton?: boolean;
}

export const PLATFORM_DOWNLOAD = {
  [SOCIAL_PLATFORMS.instagram]: {
    fileName: 'document.pdf',
    type: PDF_DOCUMENT_TYPE.CONTENT,
  },
  [SOCIAL_PLATFORMS.tiktok]: {
    fileName: 'document.pdf',
    type: PDF_DOCUMENT_TYPE.TIKTOK_CONTENT,
  },
  [SOCIAL_PLATFORMS.youtube]: {
    fileName: 'document.pdf',
    type: PDF_DOCUMENT_TYPE.YOUTUBE_CONTENT,
  },
} as const;

export const Actions = ({
  creator,
  content,
  onShare,
  trackIds,
  hideSimilarPostsButton,
}: Props) => {
  const pdf = PLATFORM_DOWNLOAD[content.platform];

  const manager = useSelector(selectTalentManager);

  const isOnboardingHooksEnabled = useFeature(
    FEATURE_FLAGS.foamContentDetailModalHooks,
  ).isEnabled;

  const { handleConversationStarterAction } = useConversationStarterAction();

  if (content) {
    return (
      <>
        <Flex justifyContent="flex-end" gap={2} marginY={'spacing-200'}>
          {!hideSimilarPostsButton &&
            isOnboardingHooksEnabled &&
            content.caption && (
              <MagicButton
                size="sm"
                magicVariant="default"
                animationLoop="once"
                iconStart="Magic"
                data-track-id={ONBOARDING_HOOKS_TRACK_ID.viewSimilarPosts}
                onClick={() => {
                  handleConversationStarterAction(
                    ONBOARDING_HOOKS_ACTION_TYPES.viewSimilarPostsHook,
                    {
                      id: content.creator?.id || '',
                      slug: content.creator?.slug || '',
                      fullName: content.creator?.fullName || '',
                      contentId: content.id,
                      contentCaptions: content.caption || '',
                    },
                    {
                      feature: 'onboarding-hooks',
                      action: 'viewSimilarPosts',
                    },
                  );
                }}
              >
                View similar posts
              </MagicButton>
            )}

          {pdf && manager && (
            <ExportPdfButton
              size="sm"
              fileName={pdf.fileName}
              leftIcon={<Icon name="Download" width={16} height={16} />}
              data={{ creator, content }}
              pdfType={pdf.type}
              track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.downloadButton]}
            >
              Download PDF
            </ExportPdfButton>
          )}

          {onShare &&
            creator.status !== 'off-platform' &&
            Boolean(content.shareLink) && ( // hide share button if there is no share link
              <Button
                iconStart="Share"
                onClick={() => onShare(content)}
                size="sm"
                data-track-id={
                  trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.shareButton]
                }
              >
                Share
              </Button>
            )}
        </Flex>
      </>
    );
  }

  return null;
};
